import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { useServices } from "@/lib/hooks";
import ServiceIcon from "@/components/ServiceIcon";
import { fileUrl } from "@/lib/api";

export default function Services() {
  const { data: services = [], isLoading } = useServices();
  const navigate = useNavigate();

  return (
    <div data-testid="services-page">
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-24">
          <span className="text-green-500 font-bold uppercase tracking-widest text-xs">Our services</span>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight max-w-3xl leading-[1.05]">
            Everything your outdoor space needs
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            Professional-grade equipment, careful methods and honest pricing. Choose a service below and request a tailored quote.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 md:py-20">
        {isLoading ? (
          <p className="text-slate-500">Loading services…</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {services.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                data-testid={`service-card-${i}`}
              >
                {s.image_url ? (
                  <img src={fileUrl(s.image_url)} alt={s.name} className="h-48 w-full object-cover" />
                ) : (
                  <div className="h-48 w-full bg-gradient-to-br from-green-50 to-slate-100 grid place-items-center">
                    <div className="h-16 w-16 rounded-2xl bg-green-600 text-white grid place-items-center">
                      <ServiceIcon name={s.icon} className="h-8 w-8" />
                    </div>
                  </div>
                )}
                <div className="p-7">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display font-bold text-slate-900 text-xl">{s.name}</h3>
                    {s.starting_price && (
                      <span className="shrink-0 rounded-full bg-green-50 px-3 py-1 text-sm font-bold text-green-700">{s.starting_price}</span>
                    )}
                  </div>
                  <p className="text-slate-500 mt-3 leading-relaxed">{s.description}</p>
                  <button
                    onClick={() => navigate(`/quote?service=${s.id}`)}
                    data-testid={`service-quote-btn-${i}`}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-bold text-white hover:bg-green-700 transition-colors"
                  >
                    Request a quote <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
